import { useState, useEffect, useCallback } from "react";
import { X, ChevronRight, ChevronLeft, HelpCircle } from "lucide-react";

function Tooltip({ step, totalSteps, currentStep, onNext, onPrev, onClose }) {
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="tour-guide-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Tooltip card */}
      <div
        className="absolute z-10 w-80 bg-[#0A0A0A] border border-[#C5A059]/40 shadow-2xl shadow-black/50"
        style={{ top: step.top || "50%", left: step.left || "50%", transform: step.transform || "translate(-50%, -50%)" }}
      >
        {/* Progress bar */}
        <div className="h-0.5 bg-white/5">
          <div className="h-full bg-[#C5A059] transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <div className="p-5">
          {/* Step indicator */}
          <div className="flex items-center justify-between mb-3">
            <span className="font-sans text-[#C5A059] text-[10px] tracking-[0.2em] uppercase">
              Step {currentStep + 1} of {totalSteps}
            </span>
            <button onClick={onClose} className="text-white/30 hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>

          {/* Icon + Title */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 border border-[#C5A059]/30 flex items-center justify-center shrink-0">
              <span className="text-lg">{step.icon}</span>
            </div>
            <h4 className="font-serif text-white text-lg font-light">{step.title}</h4>
          </div>

          {/* Description */}
          <p className="font-sans text-white/60 text-sm leading-relaxed mb-5">{step.description}</p>

          {/* Tip */}
          {step.tip && (
            <div className="bg-[#C5A059]/10 border border-[#C5A059]/20 px-3 py-2 mb-4">
              <p className="font-sans text-[#C5A059] text-xs">{step.tip}</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={onPrev}
              disabled={currentStep === 0}
              className="flex items-center gap-1 font-sans text-white/40 text-xs hover:text-white transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} /> Back
            </button>

            <button
              onClick={onNext}
              className="flex items-center gap-1 bg-[#C5A059] text-black font-sans text-xs tracking-wider uppercase px-4 py-2 hover:bg-[#A8853D] transition-colors"
              data-testid="tour-guide-next"
            >
              {currentStep === totalSteps - 1 ? "Got it!" : "Next"} <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Pointer arrow to highlighted element */}
      {step.highlightSelector && <HighlightBox selector={step.highlightSelector} />}
    </div>
  );
}

function HighlightBox({ selector }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const el = document.querySelector(selector);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect(r);
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selector]);

  if (!rect) return null;

  return (
    <div
      className="absolute border-2 border-[#C5A059] rounded-sm pointer-events-none z-[5]"
      style={{
        top: rect.top - 4 + window.scrollY,
        left: rect.left - 4,
        width: rect.width + 8,
        height: rect.height + 8,
        boxShadow: "0 0 0 9999px rgba(0,0,0,0.5), 0 0 20px rgba(197,160,89,0.3)"
      }}
    />
  );
}

const DASHBOARD_STEPS = [
  {
    icon: "👋",
    title: "Welcome to Admin",
    description: "This is your control panel for managing all tours and experiences on the Obsidian website. Let me walk you through how everything works.",
    tip: "You can restart this guide anytime by clicking the help button (?) in the top bar.",
    top: "50%", left: "50%", transform: "translate(-50%, -50%)"
  },
  {
    icon: "🔍",
    title: "Search Tours",
    description: "Type any word here to quickly find a tour. For example, type 'volcano' to find all volcano-related tours.",
    highlightSelector: '[data-testid="admin-search-input"]',
    top: "160px", left: "50%", transform: "translateX(-50%)"
  },
  {
    icon: "📂",
    title: "Filter by Category",
    description: "Use this dropdown to show only tours from a specific category like 'Day Experiences', 'Hiking', or 'Taylor Made'.",
    highlightSelector: '[data-testid="admin-cat-filter"]',
    top: "160px", left: "70%", transform: "translateX(-50%)"
  },
  {
    icon: "✏️",
    title: "Edit a Tour",
    description: "Click on any tour in the list to open its editor. There you can change texts, photos, prices, and itinerary.",
    tip: "Each tour shows: thumbnail, name, category, duration, number of photos, and price.",
    highlightSelector: '[data-testid^="admin-tour-"]',
    top: "300px", left: "50%", transform: "translateX(-50%)"
  },
  {
    icon: "➕",
    title: "Create New Tour",
    description: "Click this button to create a brand new tour from scratch. Fill in the details and click Save when done.",
    highlightSelector: '[data-testid="new-tour-btn"]',
    top: "80px", left: "70%", transform: "translateX(-50%)"
  },
  {
    icon: "🚪",
    title: "Log Out",
    description: "When you're done, click this button to securely log out of the admin panel.",
    highlightSelector: '[data-testid="admin-logout-btn"]',
    top: "80px", left: "80%", transform: "translateX(-50%)"
  }
];

const EDITOR_STEPS = [
  {
    icon: "📝",
    title: "Basic Information",
    description: "Here you edit the tour's name, URL slug, subtitle, category, duration, max guests, and main description. These are the texts visitors see on the website.",
    tip: "The 'Slug' is the URL-friendly version of the name (e.g., 'lake-ilopango-historic-suchitoto').",
    top: "50%", left: "50%", transform: "translate(-50%, -50%)"
  },
  {
    icon: "🖼️",
    title: "Cover Image",
    description: "This is the main photo that appears on the tour card. You can paste a URL or click 'Upload Image' to upload a photo from your computer.",
    tip: "For best results, use landscape photos (horizontal) at least 800px wide.",
    top: "50%", left: "50%", transform: "translate(-50%, -50%)"
  },
  {
    icon: "📸",
    title: "Photo Gallery",
    description: "Add multiple photos to the tour gallery. Click the '+' box to upload from your device, or paste a URL in the field below. To remove a photo, hover over it and click the X.",
    tip: "Visitors can browse these photos in the 'Gallery' tab when they click on a tour.",
    top: "50%", left: "50%", transform: "translate(-50%, -50%)"
  },
  {
    icon: "💰",
    title: "Pricing",
    description: "Choose the pricing type: 'Tiered' for different prices by group size, 'Call for Prices' for tours you quote individually, or 'Fixed' for a single price. Add or remove price tiers as needed.",
    tip: "For tiered pricing, add labels like '2-4 guests' with the price per person.",
    top: "50%", left: "50%", transform: "translate(-50%, -50%)"
  },
  {
    icon: "🗺️",
    title: "Itinerary (Stops)",
    description: "Add the stops/activities of the tour in order. Each stop has a title and description. Click 'Add Stop' for more. Use the trash icon to remove a stop.",
    tip: "For options (A/B), start the title with 'Option A' or 'Option B' and they'll display specially on the website.",
    top: "50%", left: "50%", transform: "translate(-50%, -50%)"
  },
  {
    icon: "⭐",
    title: "Highlights & Includes",
    description: "Highlights are the key selling points visitors see. Includes lists what's provided (transport, guide, lunch, etc). Add or remove items with the + and X buttons.",
    top: "50%", left: "50%", transform: "translate(-50%, -50%)"
  },
  {
    icon: "💾",
    title: "Save Your Changes",
    description: "When you're done editing, click the gold SAVE button at the top right. You'll see a green 'Saved!' message when successful. Changes appear on the website immediately.",
    highlightSelector: '[data-testid="save-tour-btn"]',
    tip: "Don't forget to save! Unsaved changes will be lost if you navigate away.",
    top: "80px", left: "60%", transform: "translateX(-50%)"
  }
];

export function useTourGuide(page) {
  const storageKey = `obsidian_guide_${page}`;
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [hasSeenGuide, setHasSeenGuide] = useState(false);

  const steps = page === "dashboard" ? DASHBOARD_STEPS : EDITOR_STEPS;

  useEffect(() => {
    const seen = localStorage.getItem(storageKey);
    if (seen) {
      setHasSeenGuide(true);
    } else {
      setActive(true);
    }
  }, [storageKey]);

  const close = useCallback(() => {
    setActive(false);
    setHasSeenGuide(true);
    localStorage.setItem(storageKey, "true");
  }, [storageKey]);

  const next = useCallback(() => {
    setStep(s => {
      if (s < steps.length - 1) return s + 1;
      close();
      return s;
    });
  }, [steps.length, close]);

  const prev = useCallback(() => {
    setStep(s => (s > 0 ? s - 1 : s));
  }, []);

  const restart = useCallback(() => {
    setStep(0);
    setActive(true);
  }, []);

  const GuideComponent = active ? (
    <Tooltip
      step={steps[step]}
      totalSteps={steps.length}
      currentStep={step}
      onNext={next}
      onPrev={prev}
      onClose={close}
    />
  ) : null;

  return { GuideComponent, restart, hasSeenGuide };
}

export function HelpButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="help-button"
      className="w-8 h-8 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#C5A059] hover:border-[#C5A059]/40 transition-all duration-300"
      title="Show guide"
    >
      <HelpCircle size={16} />
    </button>
  );
}
